import { forwardRef, useCallback, useEffect, useState } from "react";

import { Autocomplete, CircularProgress } from "@mui/material";
import PropTypes from "prop-types";

import { CustomInputField } from "../custom-input-field";

export const CustomAutocomplete = forwardRef(
  (
    {
      fetchOptions,
      TextFieldProps,
      value,
      onChange,
      disabled,
      getOptionLabel,
      isOptionEqualToValue,
      ...props
    },
    ref
  ) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);

  const handleFetchOptions = useCallback(async () => {
    setIsLoading(true);
    setOptions(await fetchOptions());
    setIsLoading(false);
  }, [fetchOptions]);

  useEffect(() => {
    if (!disabled) {
      handleFetchOptions();
    }
  }, [handleFetchOptions, disabled]);

  // Ensure value is included in options if it exists
  const optionsWithValue = useCallback(() => {
    if (!value) return options;

    const valueExists = options.some((option) =>
      isOptionEqualToValue(option, value)
    );

    return valueExists ? options : [value, ...options];
  }, [options, value, isOptionEqualToValue]);

  return (
      <Autocomplete
        ref={ref}
        value={value || null}
        onChange={(_, newValue) => onChange(newValue)}
        options={optionsWithValue()}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={getOptionLabel}
        loading={isLoading}
        renderInput={(params) => (
          <CustomInputField
            {...TextFieldProps}
            id={params.id}
            fullWidth={params.fullWidth}
            disabled={params.disabled}
            InputProps={{
              ...params.slotProps.input,
              ...TextFieldProps?.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.slotProps.htmlInput,
              ...TextFieldProps?.inputProps,
            }}
          />
        )}
        disabled={disabled}
        {...props}
      />
    );
  }
);

CustomAutocomplete.displayName = "CustomAutocomplete";

CustomAutocomplete.propTypes = {
  fetchOptions: PropTypes.func.isRequired,
  TextFieldProps: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  getOptionLabel: PropTypes.func.isRequired,
  isOptionEqualToValue: PropTypes.func.isRequired,
};
